/// @flow
import {BatchUser} from '@batch.com/react-native-plugin';
import {CommonActions} from '@react-navigation/native';
import {Platform} from 'react-native';
import {stopSubmit, change} from 'redux-form';
import {all, call, put, select, take, takeEvery, takeLatest, race} from 'redux-saga/effects';
import {clearToken, resetTripicaBearerToken, setTripicaBearerToken, tripica} from '../../api';
import config from '../../config';
import {fetchZendeskUrl} from '../../lib/Zendesk';
import * as Analytics from '../../lib/analytics';
import HandleErrorService from '../../lib/handleError';
import I18n from '../../lib/i18n';
import Navigation, {getCurrentRoute, resetToPath, navigate} from '../../lib/navigation';
import Toast from '../../lib/toastService';
import {fetchCustomerSaga, getCustomerOffer} from '../Customer/sagas';
import {fetchDashboardDataSaga} from '../Dashboard';
import {setError, setLoading} from '../LoadingStatus/actions';
import {resetToDashboard, resetToWelcomeScreen} from '../Navigation/actions';
import {goToNextOrderPage} from '../SignUp/actions';
import {goToNextActivationPage} from '../SignUp/sagas';
import {ALLOWED_REDIRECT_PATHS} from '../../lib/allowedRedirectPaths';
import type {AuthActionType, LoginActionType} from './actions';
import {resetCustomerData, setDeviceId, signInSuccess} from './actions';
import {isLoggedInSelector} from './selectors';
import WizzeeLog from '../../lib/wizzeeLog';

export function* createDeviceId(): Generator<*, *, *> {
  try {
    const installationId = yield call([BatchUser, 'getInstallationID']);
    const deviceId = `${Platform.OS}-${installationId}`;
    yield put(setDeviceId(deviceId));

    return deviceId;
  } catch (err) {
    WizzeeLog.warn(
      "[createDeviceId] Can't fetch installation id from Batch",
      err,
    );
    return null;
  }
}

export function* loginCall(
  credentials: {email: string, password: string},
  context: {},
  validationCode: ?string = null,
): Generator<*, *, *> {
  try {
    const {email, password} = credentials;
    const response = yield call(
      [tripica, 'login'],
      email,
      password,
      true,           // rememberMe
      null,           // language
      null,           // deviceId
      validationCode, // OTP code — null on first call, filled on second
      context,
    );
    // Sync the in-memory bearer token with the final JWT before any post-auth call.
    // When OTP is active, tripicaBearerToken holds the temporary JWT from the
    // PIN_REQUIRED 401 response. The OTP success response may not update it (Authorization
    // header absent or identical to the temp JWT → fetchAdapter success path skips the
    // update), leaving subsequent calls such as fetchCustomerSaga with a restricted
    // token → 403. Explicitly overwriting it here covers all login paths (OTP and standard).
    const tokenWithBearer = response.token.startsWith('Bearer')
      ? response.token
      : `Bearer ${response.token}`;
    setTripicaBearerToken(tokenWithBearer);
    if (Platform.OS !== 'web') {
      yield call(subscribeToNotifications, context);
      Analytics.logUser(response.externalId);
    }
    yield put(signInSuccess({token: tokenWithBearer, externalId: response.externalId}));
    return response;
  } catch (e) {
    // Parse response body to distinguish PIN_REQUIRED and SANDBOX_ERROR sub-types.
    let errorCode = null;
    let errorCodeList = null;
    try {
      if (e && typeof e.json === 'function' && !e.bodyUsed) {

        const body = yield call([e, e.json]);
        errorCode = body && body.errorCode;
        errorCodeList = body && body.errorCodeList;
        WizzeeLog.warn('[saga] loginCall - error response', e, {errorCode, errorCodeList});
      }
    } catch (_) {}
    
    if (errorCode === 'PIN_REQUIRED') {
      // A non-empty validationCode means the user submitted an actual OTP that was rejected.
      // null (first call) and '' (resend) both correctly map to the "show OTP screen" path.
      if (validationCode) {
        return {loginError: true, message: I18n.t('Login.OtpVerification.invalidCode')};
      }
      return 'PIN_REQUIRED';
    }
    
    // 400 SANDBOX_ERROR — terminal OTP states (doc technique section 4).
    // RETRIES_EXCEED checked first because the 5th attempt returns both codes.
    if (e && e.status === 400 && errorCode === 'SANDBOX_ERROR' && errorCodeList) {
      const list = Array.isArray(errorCodeList)
      ? errorCodeList.join(',')
      : String(errorCodeList);
      WizzeeLog.warn('[saga] loginCall - response SANDBOX_ERROR: ', list);
      if (list.includes('VALIDATION_RETRIES_EXCEED')) {
        return 'OTP_RETRIES_EXCEEDED';
      }
      if (list.includes('VALIDATION_DELAY_EXCEED')) {
        return 'OTP_EXPIRED';
      }
    }

    // 409 — Sandbox deduplication: a code was already sent for the same computedUuid (RF-06).
    if (e && e.status === 409) {
      return 'OTP_DEDUPLICATED';
    }

    WizzeeLog.warn('[saga] loginCall', e);
    const message = HandleErrorService.getErrorMessage(e, err => {
      if (err && err.status && [401, 403].includes(err.status)) {
        return validationCode !== null
          ? I18n.t('Login.OtpVerification.invalidCode')
          : I18n.t('invalidCredentials');
      }
      return null;
    });

    // OTP second call — let the caller handle display (inline + toast).
    if (validationCode !== null) {
      return {loginError: true, message};
    }

    yield put(stopSubmit('login', {_error: message}));
    return false;
  }
}

export function* subscribeToNotifications(context: {}): Generator<*, *, *> {
  const customer = yield call([tripica, 'getCustomer'], context);
  if (!customer) return;

  const deviceId = yield call(createDeviceId);
  if (!deviceId) return;

  yield call(
    [tripica, 'updateCustomerPreference'],
    {
      ...customer.login.customerPreference,
      deviceId,
    },
    context,
  );
}

export function* loginDuringActivate(
  action: LoginActionType,
): Generator<*, *, *> {
  if (action.type !== 'LOGIN_DURING_ACTIVATE') return; // for flow

  const context = {bypassUnauthorizedRequestCheck: true};
  yield put(setLoading('loginDuringActivate', true));
  // Pas de reset du JWT ici : triPica exige que le token déjà présent
  // (session anonyme/sandbox de l'activation) soit réutilisé sur /login/jwt,
  // sinon le backend génère un token déconnecté de la session. Le reset reste
  // uniquement dans le resend OTP (runOtpLoop), où un idTrx neuf est requis.

  try {
    const loginResult = yield call(loginCall, action.payload, context);

    if (loginResult === 'PIN_REQUIRED') {
      yield put(setLoading('loginDuringActivate', false));
      const otpSucceeded = yield call(runOtpLoop, action.payload, context);
      if (otpSucceeded) {
        yield call(getCustomerOffer);
        yield call(goToNextActivationPage, true, true);
      }
    } else if (loginResult) {
      yield call(getCustomerOffer);
      yield call(goToNextActivationPage, true, true);
    }
  } catch (e) {
    WizzeeLog.warn('[saga] loginDuringActivate', e);
    yield put(
      stopSubmit('login', {_error: HandleErrorService.getErrorMessage(e)}),
    );
  } finally {
    yield put(setLoading('loginDuringActivate', false));
    yield put(setLoading('loginWithOtp', false));
  }
}

export function* loginDuringSignUp(
  action: LoginActionType,
): Generator<*, *, *> {
  if (action.type !== 'LOGIN_DURING_SIGNUP') return; // for flow

  const context = {bypassUnauthorizedRequestCheck: true};
  yield put(setLoading('loginDuringSignUp', true));
  // Pas de reset du JWT ici : pendant le signup, tripicaBearerToken porte déjà le
  // JWT de la session anonyme/sandbox utilisé par les appels publics précédents
  // (resource, msisdn…). triPica exige que ce même JWT soit réutilisé sur /login/jwt :
  // le vider forcerait le backend à générer un nouveau token et casserait le lien
  // avec la session de signup (→ 400 sur productOrder). Le reset reste actif dans le
  // resend OTP (runOtpLoop) où un idTrx neuf est requis pour éviter la dédup Sandbox (409).

  const loginResponse = yield call(loginCall, action.payload, context);

  if (loginResponse === 'PIN_REQUIRED') {
    yield put(setLoading('loginDuringSignUp', false));
    const otpSucceeded = yield call(runOtpLoop, action.payload, context);
    if (otpSucceeded) {
      yield call(fetchCustomerSaga);
      if (config.askPersonalInformationDuringOrderSim) {
        yield put(goToNextOrderPage());
      }
    }
  } else if (loginResponse) {
    yield call(fetchCustomerSaga);
    if (config.askPersonalInformationDuringOrderSim) {
      yield put(goToNextOrderPage());
    }
  }

  yield put(setLoading('loginDuringSignUp', false));
  yield put(setLoading('loginWithOtp', false));
}

function* navigateToDashboard(redirectPath: ?string): Generator<*, *, *> {
  if (redirectPath && ALLOWED_REDIRECT_PATHS.includes(redirectPath)) {
    yield call(fetchDashboardDataSaga);
    yield call(resetToPath, redirectPath);
  } else if (Platform.OS !== 'web') {
    yield call(resetToDashboard);
  }
}

/**
 * Shared OTP loop — called after any loginCall returns PIN_REQUIRED.
 * Handles navigate → race(SUBMIT_OTP | RESEND_LOGIN_OTP | OTP_CANCELLED) until
 * success or a terminal state.
 *
 * Loading-state contract:
 *   - loginWithOtp is left TRUE on success (return true) → the caller's finally cleans it up.
 *   - loginWithOtp is set to FALSE on every non-success path (cancel, terminal, retries exceeded)
 *     before returning false, so no cleanup is needed in the caller on those paths.
 *   - loginResendOtp is always restored to false before the function returns.
 *
 * @returns true  if OTP validated (signInSuccess already dispatched by loginCall)
 * @returns false if cancelled or terminal state (goBack already dispatched)
 */
export function* runOtpLoop(
  credentials: {email: string, password: string},
  context: {},
): Generator<*, boolean, *> {
  yield put(setError('otpAttemptsRemaining', null));
  navigate('otpVerification', {email: credentials.email});

  const OTP_MAX_RETRIES = 5;
  let otpAttempts = 0;

  // takeLatest on LOGIN_REQUEST (and takeEvery on LOGIN_DURING_*) will cancel this generator
  // if a new login action is dispatched — safe to loop indefinitely here.
  while (true) { // eslint-disable-line no-constant-condition
    const result = yield race({
      submit: take('SUBMIT_OTP'),
      resend: take('RESEND_LOGIN_OTP'),
      cancel: take('OTP_CANCELLED'),
    });

    if (result.cancel) {
      yield put(setError('otpAttemptsRemaining', null));
      return false;
    }

    if (result.resend) {
      yield put(setLoading('loginResendOtp', true));
      // Clearing the JWT forces the API to generate a new idTrx, which produces a new
      // computedUuid = hash(email + idTrx) and bypasses Sandbox 409 deduplication (RF-06).
      // Keeping the JWT reuses the same idTrx → same computedUuid → 409, no email sent.
      resetTripicaBearerToken();
      const resendResult = yield call(loginCall, credentials, context);
      yield put(setLoading('loginResendOtp', false));
      if (resendResult === 'PIN_REQUIRED') {
        yield call([Toast, Toast.showMessage], I18n.t('Login.OtpVerification.codeSent'));
      } else if (resendResult === 'OTP_DEDUPLICATED') {
        // 409 — deduplicated despite a fresh idTrx (edge case, RF-06).
        HandleErrorService.showToastError(I18n.t('Login.OtpVerification.codeDeduplicated'));
      } else {
        HandleErrorService.showToastError(I18n.t('Login.OtpVerification.resendError'));
      }
      continue;
    }

    // SUBMIT path
    yield put(setLoading('loginWithOtp', true));
    const otpResponse = yield call(
      loginCall,
      credentials,
      context,
      result.submit.payload.otpCode,
    );

    // T1+T2: Terminal OTP states — show specific message and return user to origin screen.
    if (otpResponse === 'OTP_EXPIRED' || otpResponse === 'OTP_RETRIES_EXCEEDED') {
      yield put(setLoading('loginWithOtp', false));
      yield put(setError('otpAttemptsRemaining', null));
      const msgKey = otpResponse === 'OTP_EXPIRED' ? 'codeExpired' : 'tooManyAttempts';
      HandleErrorService.showToastError(I18n.t(`Login.OtpVerification.${msgKey}`));
      yield call(Navigation.dispatch, CommonActions.goBack());
      return false;
    }

    if (otpResponse && !otpResponse.loginError) {
      yield put(setError('otpAttemptsRemaining', null));
      return true; // loginWithOtp stays true — the caller's finally handles it
    }

    if (otpResponse && otpResponse.loginError) {
      otpAttempts += 1;
      const remaining = Math.max(OTP_MAX_RETRIES - otpAttempts, 0);
      yield put(setLoading('loginWithOtp', false));

      if (remaining === 0) {
        // Client-side enforcement: max retries reached.
        // Mirrors OTP_RETRIES_EXCEEDED even if the server hasn't enforced it yet
        // (e.g. DEV Sandbox with maxRetries unconfigured).
        yield put(setError('otpAttemptsRemaining', null));
        HandleErrorService.showToastError(I18n.t('Login.OtpVerification.tooManyAttempts'));
        yield call(Navigation.dispatch, CommonActions.goBack());
        return false;
      }

      yield put(setError('otpAttemptsRemaining', remaining));
      yield put(stopSubmit('otpVerification', {_error: otpResponse.message}));
      yield put(change('otpVerification', 'code', ''));
      HandleErrorService.showToastError(otpResponse.message);
    }
  }
  // $FlowFixMe — unreachable, satisfies Flow's return type check
  return false;
}

export function* login(action: LoginActionType): Generator<*, *, *> {
  if (action.type !== 'LOGIN_REQUEST') return; // for flow

  const context = {bypassUnauthorizedRequestCheck: true};
  yield put(setLoading('login', true));
  // Pas de reset du JWT ici : triPica exige que le token éventuellement présent
  // soit réutilisé sur /login/jwt (continuité de session). L'authentification
  // reste basée sur email/password (dans le body) ; le token ne sert que de
  // contexte de session. Le reset reste uniquement dans le resend OTP (runOtpLoop).
  try {
    const loginResponse = yield call(loginCall, action.payload, context);

    if (loginResponse === 'PIN_REQUIRED') {
      yield put(setLoading('login', false));
      const otpSucceeded = yield call(runOtpLoop, action.payload, context);
      if (otpSucceeded) {
        yield call(navigateToDashboard, action.payload.redirectPath);
      }
    } else if (loginResponse) {
      yield call(navigateToDashboard, action.payload.redirectPath);
    }
  } catch (e) {
    WizzeeLog.warn('[saga] login', e);
    yield put(
      stopSubmit('login', {_error: HandleErrorService.getErrorMessage(e)}),
    );
  } finally {
    yield put(setLoading('login', false));
    yield put(setLoading('loginWithOtp', false));
  }
}

export function* resetPasswordConfirmationSaga(
  action: LoginActionType,
): Generator<*, *, *> {
  if (action.type !== 'RESET_PASSWORD_CONFIRMATION') return; // for flow

  yield put(setLoading('resetPasswordConfirmation', true));
  try {
    const route = yield select(getCurrentRoute);
    const token = route.params?.token ?? route.params?.params?.token;
    if (!token) {
      throw new Error('Token not found');
    }

    yield call([tripica, 'updatePassword'], action.payload.password, token);
  } catch (e) {
    yield put(
      setError('resetPasswordConfirmation', {
        title: I18n.t('Login.ResetPasswordConfirmation.failureTitle'),
        text: I18n.t('Login.ResetPasswordConfirmation.failureText'),
      }),
    );
  } finally {
    yield put(setLoading('resetPasswordConfirmation', false));
    yield call(
      Navigation.dispatch,
      CommonActions.navigate({name: 'resetPasswordConfirmationSuccess'}),
    );
  }
}

export function* resetCustomerDataSaga(
  action: AuthActionType,
): Generator<*, *, *> {
  if (action.type !== 'RESET_CUSTOMER_DATA') return; // for flow
  try {
    const isLoggedIn = yield select(isLoggedInSelector);
    if (isLoggedIn)
      yield call([tripica, 'logout'], {bypassUnauthorizedRequestCheck: true});
  } catch (e) {
    WizzeeLog.warn('[saga] resetCustomerData', e);
  }
  yield call(clearToken);
}

export function* logOutSaga(action: AuthActionType): Generator<*, *, *> {
  try {
    yield put(resetCustomerData());
    yield call(resetToWelcomeScreen);
  } catch (error) {
    WizzeeLog.warn('[saga] logOutSaga', error);
  }
}

export function* resetPasswordSaga(action: AuthActionType): Generator<*, *, *> {
  if (action.type !== 'RESET_PASSWORD') return; // for flow
  const {email} = action.payload;

  yield put(setLoading('resetPassword', true));

  try {
    yield call([tripica, 'resetPassword'], email);
    yield call(Navigation.dispatch, CommonActions.goBack());
    yield call(Toast.showMessage, I18n.t('emailSent'));
  } catch (error) {
    WizzeeLog.warn('[saga] resetPasswordSaga', error);
    yield call(HandleErrorService.showToastError, error);
  } finally {
    yield put(setLoading('resetPassword', false));
  }
}

export function* changePasswordSaga(
  action: AuthActionType,
): Generator<*, *, *> {
  if (action.type !== 'CHANGE_PASSWORD') return; // for flow
  const {newPassword, oldPassword} = action.payload;

  yield put(setLoading('changePassword', true));

  try {
    yield call([tripica, 'changePassword'], newPassword, oldPassword);
    yield call(Navigation.dispatch, CommonActions.goBack());
    yield call(Toast.showMessage, I18n.t('passwordChanged'));
  } catch (error) {
    WizzeeLog.warn('[saga] changePasswordSaga', error);
    yield call(HandleErrorService.showToastError, error);
  } finally {
    yield put(setLoading('changePassword', false));
  }
}

export default function* (): Generator<*, *, *> {
  yield all([
    takeEvery('LOGIN_DURING_ACTIVATE', loginDuringActivate),
    takeEvery('LOGIN_DURING_SIGNUP', loginDuringSignUp),
    takeLatest('LOGIN_REQUEST', login),
    takeEvery('RESET_PASSWORD_CONFIRMATION', resetPasswordConfirmationSaga),
    takeEvery('RESET_CUSTOMER_DATA', resetCustomerDataSaga),
    takeEvery('LOG_OUT', logOutSaga),
    takeEvery('RESET_PASSWORD', resetPasswordSaga),
    takeEvery('CHANGE_PASSWORD', changePasswordSaga),
  ]);
}
